import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from '../../hooks/useApi';
import { v2Path } from '../nav';

/**
 * Copy of `src/components/NotificationBanner.tsx`, identical in logic and copy,
 * differing only in where the action navigates: `v2Path('/accounts')`.
 *
 * CRITICAL: the `sessionStorage` key and the two banner ids are byte-identical
 * to v1's, so a dismissal made in v1 stays dismissed in v2 and vice versa —
 * exactly as it does today between two v1 pages.
 *
 * Placement (DESIGN_PLAN §2.9): inside the content column, directly above
 * `PageHeader`, at the same max width as the page — not full-bleed. These
 * banners are about a fixable condition, not about the identity of the session.
 * The sidebar's `Connections` item carries a matching dot so the signal
 * survives dismissal, which in v1 it does not.
 */

interface BannerItem {
  id: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  action?: { label: string; onClick: () => void };
}

const DISMISSED_KEY = 'ft_dismissed_banners';

function getDismissed(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]');
  } catch {
    return [];
  }
}

function dismiss(id: string) {
  const dismissed = getDismissed();
  if (!dismissed.includes(id)) {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed, id]));
  }
}

/**
 * `refresh_needed_platforms` / `banned_platforms` carry one entry per affected
 * CONNECTION, so a user with three DICE logins in trouble got a banner reading
 * "DICE, AXS, DICE, DICE need to be refreshed". The platform is the actionable
 * unit here — the Accounts page is where you see which login it is — so the
 * names are deduped for display, first occurrence wins, and the verb agrees
 * with the deduped count. `useAccountAttentionCount` already counts this way.
 */
function uniquePlatforms(platforms: readonly string[] = []): string[] {
  return Array.from(new Set(platforms));
}

export const AccountHealthBanners: React.FC = () => {
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [localDismissed, setLocalDismissed] = useState<string[]>(getDismissed);

  const handleDismiss = useCallback((id: string) => {
    dismiss(id);
    setLocalDismissed((prev) => [...prev, id]);
  }, []);

  if (!profile?.account_health) return null;

  // The shell renders these on every screen, Accounts included, where
  // `View Accounts` navigated to the route already on screen and looked like a
  // dead button. On that page the message is the whole banner: the fix is
  // there, one scroll down.
  const accountsPath = v2Path('/accounts');
  const onAccountsPage =
    pathname === accountsPath || pathname.startsWith(`${accountsPath}/`);
  const viewAccounts = onAccountsPage
    ? undefined
    : { label: 'View Accounts', onClick: () => navigate(accountsPath) };

  const { account_health } = profile;
  const banners: BannerItem[] = [];

  if (account_health.has_banned_accounts) {
    const platforms = uniquePlatforms(account_health.banned_platforms);
    banners.push({
      id: 'banned_accounts',
      type: 'error',
      message: `Account${platforms.length > 1 ? 's' : ''} banned on ${platforms.join(', ')}. Reconnect or contact support.`,
      action: viewAccounts,
    });
  }

  if (account_health.has_accounts_needing_refresh) {
    const platforms = uniquePlatforms(account_health.refresh_needed_platforms);
    banners.push({
      id: 'refresh_needed',
      type: 'warning',
      message: `${platforms.join(', ')} ${platforms.length > 1 ? 'need' : 'needs'} to be refreshed. Re-login to keep syncing.`,
      action: viewAccounts,
    });
  }

  const visible = banners.filter((b) => !localDismissed.includes(b.id));
  if (visible.length === 0) return null;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pt-6 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-2">
        {visible.map((banner) => (
          <BannerRow key={banner.id} banner={banner} onDismiss={handleDismiss} />
        ))}
      </div>
    </div>
  );
};

/**
 * Derived from the same `profile.account_health` the banners read — no new hook,
 * no new call. Feeds the sidebar's `Connections` attention dot so the signal
 * outlives a banner dismissal.
 */
export function useAccountAttentionCount(): number {
  const { data: profile } = useProfile();
  const health = profile?.account_health;
  if (!health) return 0;
  const platforms = new Set<string>();
  if (health.has_banned_accounts) health.banned_platforms.forEach((p) => platforms.add(p));
  if (health.has_accounts_needing_refresh)
    health.refresh_needed_platforms.forEach((p) => platforms.add(p));
  return platforms.size;
}

const iconMap = {
  error: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8 11C7.59 11 7.25 10.66 7.25 10.25C7.25 9.84 7.59 9.5 8 9.5C8.41 9.5 8.75 9.84 8.75 10.25C8.75 10.66 8.41 11 8 11ZM8.75 8.25C8.75 8.66 8.41 9 8 9C7.59 9 7.25 8.66 7.25 8.25V5.25C7.25 4.84 7.59 4.5 8 4.5C8.41 4.5 8.75 4.84 8.75 5.25V8.25Z" fill="currentColor"/>
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.866 2.5a1 1 0 0 0-1.732 0L1.339 13a1 1 0 0 0 .866 1.5h11.59a1 1 0 0 0 .866-1.5L8.866 2.5ZM8.75 11.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.25 9V6.5a.75.75 0 0 1 1.5 0V9a.75.75 0 0 1-1.5 0Z" fill="currentColor"/>
    </svg>
  ),
  info: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8.75 11.25H7.25V7.25H8.75V11.25ZM8.75 6H7.25V4.5H8.75V6Z" fill="currentColor"/>
    </svg>
  ),
};

const styles = {
  error: {
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    text: 'text-red-400',
    icon: 'text-red-400',
    button: 'text-red-400 hover:text-red-300 hover:bg-red-500/10',
    close: 'text-red-400/60 hover:text-red-400',
  },
  warning: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    text: 'text-amber-400',
    icon: 'text-amber-400',
    button: 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10',
    close: 'text-amber-400/60 hover:text-amber-400',
  },
  info: {
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    text: 'text-blue-400',
    icon: 'text-blue-400',
    button: 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10',
    close: 'text-blue-400/60 hover:text-blue-400',
  },
};

const BannerRow: React.FC<{
  banner: BannerItem;
  onDismiss: (id: string) => void;
}> = ({ banner, onDismiss }) => {
  const s = styles[banner.type];

  return (
    <div
      className={`${s.bg} border ${s.border} rounded-lg px-4 py-3 flex items-start gap-3 sm:items-center animate-[fadeIn_0.2s_ease]`}
    >
      <span className={`${s.icon} flex-shrink-0 mt-0.5 sm:mt-0`}>{iconMap[banner.type]}</span>
      <span className={`${s.text} text-sm font-medium flex-1 min-w-0`}>{banner.message}</span>
      {banner.action && (
        <button
          onClick={banner.action.onClick}
          className={`${s.button} text-xs font-semibold px-3 py-1 rounded-md transition-colors whitespace-nowrap`}
        >
          {banner.action.label}
        </button>
      )}
      <button
        onClick={() => onDismiss(banner.id)}
        className={`${s.close} transition-colors flex-shrink-0`}
        aria-label="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
};
